import React, { useMemo, useState, useEffect } from "react";
import { useProgression } from "../../../context/ProgressionContext";

type QuizQuestion = {
  question: string;
  options: string[];
  correctIndex: number;
};

export function QuizBiaisConfirmation() {
  const questions: QuizQuestion[] = useMemo(
    () => [
      {
        question: "Le biais de confirmation, c’est...",
        options: [
          "La tendance à chercher ce qui confirme ce qu’on pense déjà.",
          "Le fait de toujours vérifier ses sources.",
          "Une technique pour convaincre plus facilement.",
        ],
        correctIndex: 0,
      },
      {
        question: "Dans quelle situation voit-on clairement ce biais ?",
        options: [
          "Quand on lit d’abord un article qui nous contredit.",
          "Quand on ignore les critiques et qu’on ne garde que les avis positifs sur un sujet.",
          "Quand on demande un avis différent avant de décider.",
        ],
        correctIndex: 1,
      },
      {
        question: "Quelle attitude aide à le limiter ?",
        options: [
          "Chercher une source opposée avant de conclure.",
          "S’en tenir aux infos qui nous rassurent.",
          "Arrêter toute lecture dès qu’on n’est pas d’accord.",
        ],
        correctIndex: 0,
      },
      {
        question: "Quel réflexe montre le biais de confirmation ?",
        options: [
          "Donner plus de poids à un commentaire parce qu’il va dans notre sens.",
          "Lister les sources pour comparer leurs points de vue.",
          "Changer d’avis après avoir lu des arguments solides.",
        ],
        correctIndex: 0,
      },
      {
        question: "Que faire quand une info nous contredit ?",
        options: [
          "Chercher ce qui la rend fausse, coûte que coûte.",
          "L’ignorer et rester sur notre idée.",
          "Lire jusqu’au bout et vérifier si des éléments sont crédibles.",
        ],
        correctIndex: 2,
      },
    ],
    []
  );

  const [current, setCurrent] = useState(0);
  const [selected, setSelected] = useState<number[]>([]);
  const [isFinished, setIsFinished] = useState(false);
  const { isGuest, setQuizScore } = useProgression();

  const total = questions.length;
  const currentQuestion = questions[current];

  function handleSelect(optionIndex: number) {
    setSelected((prev) => {
      const copy = [...prev];
      copy[current] = optionIndex;
      return copy;
    });
  }

  function handleNext() {
    if (current === total - 1) {
      setIsFinished(true);
      return;
    }
    setCurrent((c) => Math.min(c + 1, total - 1));
  }

  function handleRestart() {
    setCurrent(0);
    setSelected([]);
    setIsFinished(false);
  }

  const score = isFinished
    ? selected.reduce((acc, choice, idx) => {
        if (choice === questions[idx].correctIndex) return acc + 1;
        return acc;
      }, 0)
    : null;

  useEffect(() => {
    if (isFinished && score !== null && !isGuest) {
      const percent = Math.round((score / total) * 100);
      setQuizScore("quiz-biais-1", "biais-confirmation", percent);
    }
  }, [isFinished, score, total, isGuest, setQuizScore]);

  return (
    <div className="max-w-xl mx-auto mm-surface p-6 mt-8">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Quiz interactif
          </p>
          <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
            Comprendre le biais de confirmation
          </h2>
        </div>
        <span className="text-[11px] rounded-full px-3 py-1 border border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200">
          Question {Math.min(current + 1, total)} / {total}
        </span>
      </div>

      {!isFinished && (
        <>
          <p className="text-sm text-slate-700 dark:text-slate-200 mb-3">{currentQuestion.question}</p>
          <div className="space-y-2">
            {currentQuestion.options.map((opt, idx) => {
              const isSelected = selected[current] === idx;
              return (
                <button
                  key={opt}
                  type="button"
                  onClick={() => handleSelect(idx)}
                  className={`w-full text-left rounded-lg border px-3 py-2 text-sm transition ${
                    isSelected
                      ? "border-blue-500 bg-blue-50 text-blue-800 dark:border-blue-500 dark:bg-blue-900/40 dark:text-blue-100"
                      : "border-slate-200 bg-white text-slate-700 hover:border-blue-200 hover:bg-blue-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:hover:border-blue-500/50 dark:hover:bg-slate-800/80"
                  }`}
                >
                  {opt}
                </button>
              );
            })}
          </div>

          <div className="flex justify-end mt-4">
            <button
              type="button"
              onClick={handleNext}
              disabled={selected[current] === undefined}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold ${
                selected[current] === undefined
                  ? "bg-slate-200 text-slate-500 cursor-not-allowed dark:bg-slate-800 dark:text-slate-500"
                  : "bg-blue-600 text-white hover:bg-blue-500"
              }`}
            >
              {current === total - 1 ? "Terminer" : "Question suivante"}
            </button>
          </div>
        </>
      )}

      {isFinished && (
        <div className="space-y-3">
          <div className="text-center">
            <p className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              Tu as {score} / {total} bonnes réponses 👏
            </p>
            <p className="text-sm text-slate-600 dark:text-slate-300">
              {score !== null && score >= Math.ceil(total * 0.7)
                ? "Tu as bien compris les bases du biais de confirmation."
                : "Tu peux refaire le micro-cours pour consolider, puis retenter le quiz."}
            </p>
          </div>
          <div className="flex justify-center">
            <button
              type="button"
              onClick={handleRestart}
              className="px-4 py-1.5 rounded-full border border-slate-300 text-sm font-medium text-slate-700 bg-white hover:bg-slate-50 dark:border-slate-600 dark:text-slate-100 dark:bg-slate-800 dark:hover:bg-slate-700"
            >
              Rejouer le quiz
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

